import React, { useEffect, useState } from "react";
import { Icon } from "@iconify/react/dist/iconify.js";
import { apiCall } from "../utils/apiService";
import "./ClientFeatures.css";

const emptyForm = { id: null, title: "", message: "", sort_order: 0 };

const QuickRepliesLayer = () => {
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [alert, setAlert] = useState(null);

  const showAlert = (message, cls = "success") => {
    setAlert({ message, class: cls });
    setTimeout(() => setAlert(null), 2500);
  };

  const load = async () => {
    setLoading(true);
    try {
      const res = await apiCall({ url: "features/quick-replies", method: "GET" });
      if (res?.success) setRows(res.data || []);
    } catch (err) {
      showAlert("Failed to load quick replies", "danger");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    document.title = "Quick Replies";
    load();
  }, []);

  const resetForm = () => setForm(emptyForm);

  const startEdit = (row) => {
    setForm({
      id: row.id,
      title: row.title || "",
      message: row.message || "",
      sort_order: row.sort_order || 0,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.message.trim()) {
      showAlert("Title and message are required", "danger");
      return;
    }
    setSaving(true);
    try {
      const response = await apiCall({
        url: form.id ? "features/quick-replies/update" : "features/quick-replies/add",
        method: "POST",
        body: {
          id: form.id,
          title: form.title.trim(),
          message: form.message.trim(),
          sort_order: Number(form.sort_order) || 0,
        },
      });
      if (response?.success) {
        showAlert(response.msg || "Saved");
        resetForm();
        load();
      } else {
        showAlert(response?.msg || "Could not save", "danger");
      }
    } catch (err) {
      showAlert(err.message || "Server error", "danger");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (row) => {
    if (!window.confirm(`Delete “${row.title}”?`)) return;
    try {
      const response = await apiCall({
        url: "features/quick-replies/del",
        method: "POST",
        body: { id: row.id },
      });
      if (response?.success) {
        showAlert(response.msg);
        if (form.id === row.id) resetForm();
        load();
      } else showAlert(response?.msg || "Failed", "danger");
    } catch (err) {
      showAlert(err.message, "danger");
    }
  };

  return (
    <div className="cf-wrap">
      {alert && (
        <div className={`alert alert-${alert.class} cf-alert`}>{alert.message}</div>
      )}

      <div className="row g-3">
        <div className="col-lg-5">
          <div className="cf-card">
            <div className="cf-card-head">
              <h5>{form.id ? "Edit quick reply" : "New quick reply"}</h5>
              <p>Saved snippets appear as chips in the inbox composer.</p>
            </div>
            <form onSubmit={handleSubmit} className="cf-form">
              <label>
                Title
                <input
                  className="form-control"
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  placeholder="Greeting"
                  required
                />
              </label>
              <label>
                Message
                <textarea
                  className="form-control"
                  rows={4}
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  placeholder="Hi! Thanks for reaching out…"
                  required
                />
              </label>
              <label>
                Sort order
                <input
                  type="number"
                  className="form-control"
                  value={form.sort_order}
                  onChange={(e) => setForm({ ...form, sort_order: e.target.value })}
                />
              </label>
              <div className="cf-actions">
                <button type="submit" className="btn btn-success" disabled={saving}>
                  {saving ? "Saving…" : form.id ? "Update" : "Add reply"}
                </button>
                {form.id && (
                  <button type="button" className="btn btn-light" onClick={resetForm}>
                    Cancel
                  </button>
                )}
              </div>
            </form>
          </div>
        </div>

        <div className="col-lg-7">
          <div className="cf-card">
            <div className="cf-card-head">
              <h5>Saved replies</h5>
              <p>{loading ? "Loading…" : `${rows.length} snippet${rows.length === 1 ? "" : "s"}`}</p>
            </div>
            {!loading && rows.length === 0 && (
              <div className="cf-empty">
                <Icon icon="mdi:flash-outline" width={36} />
                <p>No quick replies yet. Add a greeting or FAQ answer.</p>
              </div>
            )}
            <div className="cf-list">
              {rows.map((row) => (
                <article key={row.id} className="cf-item">
                  <div className="cf-item-main">
                    <div className="cf-item-title">
                      <strong>{row.title}</strong>
                    </div>
                    <p className="cf-preview">{row.message}</p>
                  </div>
                  <div className="cf-item-actions">
                    <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => startEdit(row)}>
                      Edit
                    </button>
                    <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => remove(row)}>
                      Delete
                    </button>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuickRepliesLayer;
